/**
 * The editor group an interaction belongs to, which is also the field the event reports it in.
 *
 * `editorOther` is the remainder — an interaction the browser counts that is neither typing nor
 * pointing — so the three groups together cover whatever the browser calls an interaction.
 */
export type EditorInteractionGroupName = 'editorOther' | 'editorPointer' | 'editorTyping';

export type InteractionEventKind = {
	/**
	 * Whether an interaction is counted when this event arrives. One event of each group has it,
	 * because an interaction is several events — `keydown`, what the key produced, `keyup`, or
	 * `pointerdown`, `pointerup`, `click` — and counting each would count it three times.
	 */
	counts?: boolean;
	group: EditorInteractionGroupName;
};

/**
 * The events an interaction is made of, and the group they belong to. Both sides of the collector
 * read this, so counting and grouping cannot disagree.
 *
 * These are the events Event Timing can report with a non-zero `interactionId`, so any of them can
 * be the one an entry measured — and it is usually not the one the interaction is counted on: for a
 * pointer press the slow part is normally the `click` handler.
 *
 * The counted event is at a different end of the interaction in each group, because what can go
 * wrong differs. Typing is counted on `keydown`, since the browser opens a new interaction for
 * every repeat of a held key, and nothing cancels a key press. Pointing is counted on `pointerup`,
 * since a press cannot repeat but can be taken over by a scroll — which the browser does not count
 * either, and in that case `pointerup` never arrives.
 */
const INTERACTION_EVENTS: Record<string, InteractionEventKind> = {
	keydown: { group: 'editorTyping', counts: true },
	keyup: { group: 'editorTyping' },
	// Only while an IME composes: the browser counts the text it commits in the interaction of the
	// key that caused it.
	input: { group: 'editorTyping' },
	pointerdown: { group: 'editorPointer' },
	pointerup: { group: 'editorPointer', counts: true },
	click: { group: 'editorPointer' },
	// Ends a pointer press the way `pointerup` does, so the browser counts it as an interaction.
	contextmenu: { group: 'editorPointer' },
};

/** Derived, so the types listened for cannot drift from the table. */
export const INTERACTION_EVENT_TYPES: string[] = Object.keys(INTERACTION_EVENTS);

export function interactionEventKind(type: string): InteractionEventKind | undefined {
	return INTERACTION_EVENTS[type];
}
